import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { Image } from '@skytech/ui/Image'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import ImageOffIcon from 'mdi-react/ImageOffIcon'
import MinusIcon from 'mdi-react/MinusIcon'
import PlusIcon from 'mdi-react/PlusIcon'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { useIsAdmin } from '@/hooks/access'
import { parseInStock } from '@/modules/ecom-policy/helpers'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import { imagePlaceholder } from '@/util/misc'

const Product: FC<{
  id: number
  name: string
  alias: string | null
  imgUrl: string | null
  inStock: string | null
  price: number
  selectedProductId: number | null
  currency: string | null
  quantity: number
  quantityLimit: number
  onSelectProduct: () => void
  onChangeQuantity: (quantity: number) => void
}> = ({
  id,
  name,
  alias,
  imgUrl,
  price,
  inStock,
  currency,
  selectedProductId,
  quantity,
  quantityLimit,
  onChangeQuantity,
  onSelectProduct,
}) => {
  const [searchQuery] = useShopNewOrderContext().searchQuery

  const isSelected = selectedProductId === id
  const isAdmin = useIsAdmin()
  const isQuantitySelectionAllowed = isAdmin || quantityLimit > 1
  const isQuantitySelectionAvailable = isSelected && isQuantitySelectionAllowed
  const isQuantityLimitReached = quantity + 1 > quantityLimit

  return (
    <button
      className={`inventory__products__product ${isSelected ? 'selected' : 'not-selected'}`}
      onClick={onSelectProduct}
    >
      <div className="img">
        {imgUrl ? (
          <Image src={imgUrl} alt="product" onError={imagePlaceholder} />
        ) : (
          <ImageOffIcon className="missing-image-placeholder" />
        )}
      </div>

      {isQuantitySelectionAvailable && (
        <div className="absolute right-[11px] top-9 flex flex-col justify-center text-lg">
          <Button
            disabled={isQuantityLimitReached}
            className="m-0 p-0 text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300"
            variant="tertiary"
            Icon={<PlusIcon className="m-0" />}
            onClick={e => {
              e.stopPropagation()
              onChangeQuantity(quantity + 1)
            }}
          />
          <div className="text-green-700">{quantity}</div>
          <Button
            className="m-0 p-0 text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300"
            variant="tertiary"
            Icon={<MinusIcon className="!m-0" />}
            onClick={e => {
              e.stopPropagation()
              if (quantity === 1) {
                onSelectProduct()
                return
              }
              onChangeQuantity(quantity - 1)
            }}
          />
        </div>
      )}

      <div className="name">
        <Highlight pattern={searchQuery} text={alias || name} />
      </div>

      <div className="price">
        <FormattedNumberWithCurrency amount={price} currency={currency} />
      </div>

      <div className="in-stock">
        {parseInStock(inStock)} <Translation id="in stock" />
      </div>

      <div className="checkmark">
        {isSelected ? <CheckboxMarkedCircleOutlineIcon /> : <CheckboxBlankCircleOutlineIcon />}
      </div>
    </button>
  )
}

export default Product
